import { useMemo, useState } from "react";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Link from "@amzn/meridian/link";
import exportLargeTokens from "@amzn/meridian-tokens/base/icon/export-large";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import { SourceAttribution } from "src/components/pages/home/store/types";
import "./sourcesDropdown.scss";
import { DocumentInfo } from "./types";

interface SourcesDropdownProps {
  relatedDocuments: DocumentInfo[];
  /**
   * Citation attributions for the answer. When provided, the panel renders each
   * document under its `[n]` reference number so users can trace an inline
   * citation marker back to its source. `SourceAttribution` already carries
   * `title` and `url`, so no lookup against `relatedDocuments` is needed.
   * When absent (or when no attribution carries a citation number) the panel
   * falls back to an unnumbered list of `relatedDocuments`.
   */
  sourceAttributions?: SourceAttribution[];
}

/** A single `[n]` group: one citation number and the document(s) behind it. */
type NumberedSource = {
  citationNumber: number;
  documents: DocumentInfo[];
};

const SourcesDropdown = ({
  relatedDocuments,
  sourceAttributions,
}: SourcesDropdownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const numberedSources = useMemo<NumberedSource[] | null>(() => {
    if (!sourceAttributions || sourceAttributions.length === 0) {
      return null;
    }

    // Group by citation number. Multiple attributions can share a number when
    // the same source backs several passages of the answer, so de-duplicate by
    // url within each group.
    const documentsByNumber = new Map<number, DocumentInfo[]>();

    for (const attribution of sourceAttributions) {
      const { citationNumber, title, url } = attribution;
      if (typeof citationNumber !== "number" || !url) {
        continue;
      }

      const documents = documentsByNumber.get(citationNumber) ?? [];
      if (!documents.some((document) => document.url === url)) {
        documents.push({ title: title || url, url });
      }
      documentsByNumber.set(citationNumber, documents);
    }

    if (documentsByNumber.size === 0) {
      return null;
    }

    return Array.from(documentsByNumber.entries())
      .sort(([a], [b]) => a - b)
      .map(([citationNumber, documents]) => ({ citationNumber, documents }));
  }, [sourceAttributions]);

  const hasFallbackDocuments = relatedDocuments && relatedDocuments.length > 0;

  if (!numberedSources && !hasFallbackDocuments) {
    return null;
  }

  const renderDocumentLink = (docInfo: DocumentInfo) => (
    <Link
      href={docInfo.url}
      type="secondary"
      target="_blank"
      rel="noopener noreferrer"
    >
      {docInfo.title}
      {/* <Icon className="icon" tokens={exportLargeTokens} color="primary" /> */}
    </Link>
  );

  return (
    <div className="sources-dropdown">
      <Button
        type="secondary"
        size="medium"
        onClick={() => setIsExpanded(!isExpanded)}
        className="sources-dropdown-toggle"
        aria-expanded={isExpanded}
        aria-controls="sources-dropdown-panel"
      >
        Sources
      </Button>

      {isExpanded && (
        <div className="sources-dropdown-content" id="sources-dropdown-panel">
          <ul className="sources-list">
            {numberedSources
              ? numberedSources.map(({ citationNumber, documents }) => (
                  <li
                    key={citationNumber}
                    className="source-item source-item--numbered"
                  >
                    <span className="source-item__number">
                      [{citationNumber}]
                    </span>
                    <span className="source-item__documents">
                      {documents.map((docInfo) => (
                        <span
                          key={docInfo.url}
                          className="source-item__document"
                        >
                          {renderDocumentLink(docInfo)}
                        </span>
                      ))}
                    </span>
                  </li>
                ))
              : relatedDocuments.map((docInfo, index) => (
                  <li key={index} className="source-item">
                    <Icon
                      className="icon"
                      tokens={documentTokens}
                      color="primary"
                    />
                    {renderDocumentLink(docInfo)}
                  </li>
                ))}
          </ul>
          <div className="sources-dropdown-header">
            <button
              className="sources-dropdown-close"
              onClick={() => setIsExpanded(false)}
              aria-label="Close sources"
              type="button"
            >
              <Icon tokens={closeMediumTokens} color="secondary" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcesDropdown;
